/* Timestamps and numbers, in the reader's language.
 *
 * This replaces the hand-rolled month table that `shared/api/http/map/format.ts`
 * carried. Three shapes of the same instant survive the move unchanged, because
 * each was a deliberate choice about what question is being asked:
 *
 *   `20 Jul 2026, 09:14`  a case header — absolute, because "created" is a fact
 *                         someone may need to quote in a ticket.
 *   `2 min. ago`          last activity — relative, because the only question
 *                         being asked is "is this moving?".
 *   `Today · 09:42`       a timeline row — relative day, absolute clock, because
 *                         a sequence is read for its order and its gaps at once.
 *
 * A fourth joined them later, from `shared/lib/date.ts` rather than from the
 * mapper: `Friday, 14 August 2026`, the sub-line on a screen that reports on
 * today. It was fixed to `en-GB` where it lived and stayed English on a German
 * screen, which is the whole reason renderings of an instant belong here.
 *
 * A fifth arrived from Pixie's conversation log for the same reason: the bare
 * clock, `09:42`, which was being read off the *operating system's* locale and
 * so put a 12-hour time in the corner of a 24-hour cockpit.
 *
 * What changes is that `Intl` decides the wording. `20. Jul 2026, 09:14` and
 * `vor 2 Min.` are not translations of the English forms; they are what those
 * dates are called in German, including the ordinal dot and the leading
 * preposition, neither of which a string table would have got right.
 *
 * All of them render in the reader's local zone, which is what `Date`'s accessors
 * and `Intl` do by default. An operator in Frankfurt reading `09:14` means their
 * 09:14; showing them UTC and letting them subtract is how a delivery date gets
 * missed by an hour.
 *
 * Nothing here throws. An unparseable timestamp renders as an em dash, because a
 * screen that fails to draw because one row had a bad date is worse than a
 * screen with one dash in it.
 */

import type { TFunction } from 'i18next';

import type { Locale } from '@/shared/i18n/locale';

/** The dash the cockpit uses for "no value", not a hyphen. */
export const DASH = '—';

/** What separates a relative day from its clock: `Today · 09:42`. Punctuation
    rather than wording, so it is the same in both languages and stays in code
    where a translator cannot accidentally drop the spaces around it.

    Exported because `vocabulary.ts` joins a halted case's state to the word
    `halted` with the same separator, and two copies of a middle dot with spaces
    around it is exactly the sort of thing that drifts to one without. */
export const SEPARATOR = ' · ';

/* English here means British English, not American.
 *
 * `en-US` would render `Jul 20, 2026` — month first — while German renders
 * `20. Jul 2026`. Switching language would then reorder the date fields, and an
 * operator comparing two screenshots would have to re-read which number is the
 * day. `en-GB` is day-first like German, so only the words change. */
const TAGS: Readonly<Record<Locale, string>> = {
  en: 'en-GB',
  de: 'de-DE',
};

/* `Intl` formatters are expensive to construct and free to reuse, and these are
   called once per table cell. Cached by locale, which is the only thing that
   varies — there are two. The percent and fixed-decimal caches are keyed by
   locale *and* digits, since a 0-digit and a 1-digit formatter are different
   objects. */
const caches = {
  stamp: new Map<Locale, Intl.DateTimeFormat>(),
  date: new Map<Locale, Intl.DateTimeFormat>(),
  today: new Map<Locale, Intl.DateTimeFormat>(),
  dayMonth: new Map<Locale, Intl.DateTimeFormat>(),
  clock: new Map<Locale, Intl.DateTimeFormat>(),
  relative: new Map<Locale, Intl.RelativeTimeFormat>(),
  decimal: new Map<Locale, Intl.NumberFormat>(),
  fixed: new Map<string, Intl.NumberFormat>(),
  percent: new Map<string, Intl.NumberFormat>(),
};

function cached<K, T>(cache: Map<K, T>, key: K, make: () => T): T {
  const existing = cache.get(key);
  if (existing !== undefined) return existing;
  const made = make();
  cache.set(key, made);
  return made;
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

/** How far apart two instants are, in the coarsest unit that still says
    something. Minutes for the first hour, hours for the first day, then days,
    then weeks — nobody reading a queue needs to know something happened 4,271
    minutes ago, and a number that precise reads as more meaningful than it is. */
function coarsen(minutes: number): { readonly value: number; readonly unit: Intl.RelativeTimeFormatUnit } {
  if (minutes < 60) return { value: minutes, unit: 'minute' };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { value: hours, unit: 'hour' };
  const days = Math.floor(hours / 24);
  if (days < 7) return { value: days, unit: 'day' };
  return { value: Math.floor(days / 7), unit: 'week' };
}

/** Every rendering the screens do of an instant or a number, bound to one
 *  language. Built by `createFormatter` and handed out by `useFormatter`. */
export interface Formatter {
  /** `20 Jul 2026, 09:14` — the absolute form, for headers. */
  stamp(iso: string | null | undefined): string;
  /** `12 Jun 2026` — the same instant with the clock dropped, for things whose
      unit is a day: when a template was published, when a policy took effect.
      Showing `00:00` there would imply the minute was meaningful. */
  date(iso: string | null | undefined): string;
  /** `Friday, 14 August 2026` / `Freitag, 14. August 2026` — the sub-line on a
      screen that reports on today. The only date form that spells the weekday
      out, because it is read as prose rather than scanned down a column, and the
      only one that takes a `Date` rather than an ISO string: what it renders is
      the reader's clock, not a value off the wire. */
  today(now?: Date): string;
  /** `09:42` — the clock on its own, 24-hour in both languages. For a timestamp
      whose day the surrounding context has already established: the marker on a
      Pixie turn, which is minutes old by construction. Takes a `Date` for the
      same reason `today` does, and exists for a sharper one — the alternative is
      `toLocaleTimeString([], …)`, which reads the *machine's* locale rather than
      the reader's and puts `02:30 PM` in the corner of a 24-hour cockpit. */
  clock(at?: Date): string;
  /** `2 min. ago` / `vor 2 Min.` — the relative form, for "last activity".
      Anything in the future renders as "just now" rather than as a negative: a
      few seconds of clock skew between the API and the browser is normal, and
      `-1 min. ago` would make it look like a bug in the data. */
  relative(iso: string | null | undefined, now?: Date): string;
  /** `Today · 09:42`, `Yesterday · 15:53`, `20 Jul · 09:14` — the timeline form.
      Compared by calendar date rather than elapsed hours: an event at 23:50 last
      night is "Yesterday" at 00:30 even though it was forty minutes ago, because
      that is what the reader means by yesterday. */
  when(iso: string | null | undefined, now?: Date): string;
  /** A count or a measurement, grouped for the reader — `1,204` / `1.204`.
   *
   *  `fractionDigits` pins the decimals open and closed, the way `toFixed` does:
   *  a confidence of 0.9 has to print as `0.90` beside one of 0.91, or a column
   *  of them stops lining up and starts looking like different precisions of the
   *  same measurement. Left out, `Intl` decides, which is what a plain count
   *  wants. */
  number(value: number | null | undefined, fractionDigits?: number): string;
  /** A rate held as 0–1 rendered as a percentage. Null renders as a dash rather
      than `0%`, because "we do not know" and "none" are different claims. */
  percent(value: number | null | undefined, fractionDigits?: number): string;
}

export function createFormatter(locale: Locale, t: TFunction): Formatter {
  const tag = TAGS[locale];

  const stampFormat = cached(caches.stamp, locale, () =>
    new Intl.DateTimeFormat(tag, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }),
  );
  const dateFormat = cached(caches.date, locale, () =>
    new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', year: 'numeric' }),
  );
  const todayFormat = cached(caches.today, locale, () =>
    new Intl.DateTimeFormat(tag, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  );
  const dayMonthFormat = cached(caches.dayMonth, locale, () =>
    new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short' }),
  );
  const clockFormat = cached(caches.clock, locale, () =>
    new Intl.DateTimeFormat(tag, { hour: '2-digit', minute: '2-digit', hour12: false }),
  );
  const relativeFormat = cached(caches.relative, locale, () =>
    /* `short`, not `narrow`. English renders the two identically — `2 min ago`
       either way — but German narrow collapses minutes to a bare `vor 2 m`,
       which reads as metres. `short` gives `vor 2 Min.` and costs English
       nothing. */
    new Intl.RelativeTimeFormat(tag, { numeric: 'always', style: 'short' }),
  );
  const decimalFormat = cached(caches.decimal, locale, () => new Intl.NumberFormat(tag));

  return {
    stamp(iso) {
      const at = parse(iso);
      return at === null ? DASH : stampFormat.format(at);
    },

    date(iso) {
      const at = parse(iso);
      return at === null ? DASH : dateFormat.format(at);
    },

    today(now = new Date()) {
      return todayFormat.format(now);
    },

    clock(at = new Date()) {
      return clockFormat.format(at);
    },

    relative(iso, now = new Date()) {
      const at = parse(iso);
      if (at === null) return DASH;

      const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
      if (minutes < 1) return t('time.justNow');

      const { value, unit } = coarsen(minutes);
      return relativeFormat.format(-value, unit);
    },

    when(iso, now = new Date()) {
      const at = parse(iso);
      if (at === null) return DASH;

      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thatDay = new Date(at.getFullYear(), at.getMonth(), at.getDate());
      const days = Math.round((midnight.getTime() - thatDay.getTime()) / 86_400_000);

      const clock = clockFormat.format(at);
      if (days === 0) return `${t('time.today')}${SEPARATOR}${clock}`;
      if (days === 1) return `${t('time.yesterday')}${SEPARATOR}${clock}`;
      return `${dayMonthFormat.format(at)}${SEPARATOR}${clock}`;
    },

    number(value, fractionDigits) {
      if (value === null || value === undefined) return DASH;
      if (fractionDigits === undefined) return decimalFormat.format(value);
      return cached(caches.fixed, `${locale}:${fractionDigits}`, () =>
        new Intl.NumberFormat(tag, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }),
      ).format(value);
    },

    percent(value, fractionDigits = 0) {
      if (value === null || value === undefined) return DASH;
      return cached(caches.percent, `${locale}:${fractionDigits}`, () =>
        new Intl.NumberFormat(tag, {
          style: 'percent',
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }),
      ).format(value);
    },
  };
}
